/*
  This file loads the Segment IO SDK for FE Apps to
  send tracking data to your Segment account "source"
  https://segment.com/docs/sources/website/analytics.js/quickstart/
*/

// Type definitions for Segment Analytics
interface SegmentAnalytics {
  initialize?: boolean
  invoked?: boolean
  methods?: string[]
  factory?: (method: string) => Function
  push?: (args: any[]) => void
  load?: (writeKey: string, options?: any) => void
  SNIPPET_VERSION?: string
  _loadOptions?: any
  ready: (callback: () => void) => void
  identify: (
    userId?: string,
    traits?: any,
    options?: any,
    callback?: Function
  ) => void
  page: (
    category?: string,
    name?: string,
    properties?: any,
    options?: any,
    callback?: Function
  ) => void
  track: (
    event: string,
    properties?: any,
    options?: any,
    callback?: Function
  ) => void
  [key: string]: any
}

declare global {
  interface Window {
    analytics?: SegmentAnalytics | any[]
  }
}

function init(writekey: string): Promise<SegmentAnalytics> {
  // Make "analytics" SDK available on the window.
  if (typeof window !== 'undefined') {
    /* SEGMENT CODE START */
    /* eslint-disable */
    ;(function () {
      var analytics: any = (window.analytics = window.analytics || [])
      if (!analytics.initialize)
        if (analytics.invoked)
          window.console &&
            console.error &&
            console.error('Segment snippet included twice.')
        else {
          analytics.invoked = !0
          analytics.methods = [
            'trackSubmit',
            'trackClick',
            'trackLink',
            'trackForm',
            'pageview',
            'identify',
            'reset',
            'group',
            'track',
            'ready',
            'alias',
            'debug',
            'page',
            'once',
            'off',
            'on',
          ]
          analytics.factory = function (t: string) {
            return function () {
              var e = Array.prototype.slice.call(arguments)
              e.unshift(t)
              analytics.push(e)
              return analytics
            }
          }
          for (var t = 0; t < analytics.methods.length; t++) {
            var e = analytics.methods[t]
            analytics[e] = analytics.factory(e)
          }
          analytics.load = function (t: string, e?: any) {
            var n = document.createElement('script')
            n.type = 'text/javascript'
            n.async = !0
            n.src =
              'https://cdn.segment.com/analytics.js/v1/' +
              t +
              '/analytics.min.js'
            var a = document.getElementsByTagName('script')[0]
            a.parentNode!.insertBefore(n, a)
            analytics._loadOptions = e
          }
          analytics.SNIPPET_VERSION = '4.1.0'

          // Initialize and begin to load Segment to the browser.
          analytics.load(writekey)
        }
    })()
    /* eslint-enable */
    /* SEGMENT CODE END */
  }

  // Return promise in
  // case its needed.
  return ready().catch(e => {
    console.warn(e)
    return {} as SegmentAnalytics // Return empty analytics object on error
  })
}

// Promisified window.analytics.ready() function
// https://segment.com/docs/sources/website/analytics.js/#ready
function ready(): Promise<SegmentAnalytics> {
  return new Promise((resolve, reject) => {
    try {
      if (
        typeof window !== 'undefined' &&
        typeof window.analytics !== 'undefined' &&
        !Array.isArray(window.analytics)
      ) {
        // When ready, resolve.
        window.analytics.ready(() =>
          resolve(window.analytics as SegmentAnalytics)
        )
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject(
          'Segment is not available. Make sure you initialize first and on the client, not the server.'
        )
      }
    } catch (e) {
      reject(e)
    }
  })
}

// https://segment.com/docs/sources/website/analytics.js/#identify
function identify(
  userId?: string,
  traits?: any,
  options?: any,
  callback?: Function
): Promise<void> {
  return ready()
    .then(analytics => analytics.identify(userId, traits, options, callback))
    .catch(e => console.warn(e))
}

// https://segment.com/docs/sources/website/analytics.js/#page
function page(
  category?: string,
  name?: string,
  properties?: any,
  options?: any,
  callback?: Function
): Promise<void> {
  return ready()
    .then(analytics =>
      analytics.page(category, name, properties, options, callback)
    )
    .catch(e => console.warn(e))
}

// https://segment.com/docs/sources/website/analytics.js/#track
function track(
  event: string,
  properties?: any,
  options?: any,
  callback?: Function
): Promise<void> {
  return ready()
    .then(analytics => analytics.track(event, properties, options, callback))
    .catch(e => console.warn(e))
}

export { init, ready, identify, page, track }
